import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Row, Col, Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { FaArrowRotateRight } from "react-icons/fa6";
import DatePicker from 'components/backoffice/dateTimePicker';
import Button from 'components/backoffice/buttonCustom';
import { getOrderStatus } from 'constants';
import SearchEditorialInput from 'components/backoffice/customInput/SearchEditorialInput';
import SearchLibraryInputForFilters from 'components/backoffice/customInput/SearchLibraryInputForFilters';
import './OrderControlFilters.scss';

const OrderControlFilters = ({ filters, setFilters }) => {
  const { t } = useTranslation(['backoffice']);

  // Estados para mantener los valores seleccionados en formato de objeto
  const [editorialSelected, setEditorialSelected] = useState(null);
  const [bibliotecaSelected, setBibliotecaSelected] = useState(null);

  // Sincronizar valores cuando se limpian los filtros
  useEffect(() => {
    if (!filters.editorial) {
      setEditorialSelected(null);
    }
  }, [filters.editorial]);

  useEffect(() => {
    if (!filters.biblioteca) {
      setBibliotecaSelected(null);
    }
  }, [filters.biblioteca]);

  // Estados de pedido usando las constantes
  const estadoOptions = [
    { id: '', value: t('filters.totalEstados', { ns: 'backoffice' }) },
    ...getOrderStatus(t).map(status => ({
      id: status.id,
      value: status.value
    }))
  ];

  // Función para resetear los filtros
  const handleResetFilters = () => {
    setEditorialSelected(null);
    setBibliotecaSelected(null);
    setFilters({
      ean: '',
      editorial: '',
      fechaPedido: null,
      titulo: '',
      biblioteca: '',
      autor: '',
      estado: ''
    });
  };

  return (
    <div className="order-control-filters">
      <Row className="order-control-filters__row">
        {/* Columna 1 */}
        <Col md={4} className="order-control-filters__col">
          <div className="order-control-filters__field">
            <Form.Group>
              <Form.Label className="order-control-filters__label">
                {t('filters.ean', { ns: 'backoffice' })}
              </Form.Label>
              <Form.Control
                type="text"
                value={filters.ean}
                onChange={(e) => setFilters({ ...filters, ean: e.target.value })}
                className="order-control-filters__input"
              />
            </Form.Group>
          </div>
          <div className="order-control-filters__field">
            <Form.Group>
              <SearchEditorialInput
                showLabel={t('filters.editorial', { ns: 'backoffice' })}
                onSelected={(selected) => {
                  setEditorialSelected(selected);
                  setFilters({ ...filters, editorial: selected ? (selected.nombre_fiscal || selected.label || '') : '' });
                }}
                value={editorialSelected}
                defaultEditorial={null}
              />
            </Form.Group>
          </div>
          <div className="order-control-filters__field">
            <div className="order-control-filters__field-with-button">
              <Form.Group className="order-control-filters__date-group">
                <Form.Label className="order-control-filters__label">
                  {t('filters.fechaPedido', { ns: 'backoffice' })}
                </Form.Label>
                <DatePicker
                  value={filters.fechaPedido}
                  onChange={(date) => setFilters({ ...filters, fechaPedido: date })}
                  dateFormat="dd/MM/yyyy"
                  placeholderText=""
                  className="order-control-filters__datepicker-input form-control"
                />
              </Form.Group>
            </div>
          </div>
        </Col>

        {/* Columna 2 */}
        <Col md={4} className="order-control-filters__col">
          <div className="order-control-filters__field">
            <Form.Group>
              <Form.Label className="order-control-filters__label">
                {t('filters.titulo', { ns: 'backoffice' })}
              </Form.Label>
              <Form.Control
                type="text"
                value={filters.titulo}
                onChange={(e) => setFilters({ ...filters, titulo: e.target.value })}
                className="order-control-filters__input"
              />
            </Form.Group>
          </div>
          <div className="order-control-filters__field">
            <Form.Group>
              <SearchLibraryInputForFilters
                showLabel={t('filters.biblioteca', { ns: 'backoffice' })}
                onSelected={(selected) => {
                  setBibliotecaSelected(selected);
                  setFilters({ ...filters, biblioteca: selected ? (selected.denominacion || selected.label || '') : '' });
                }}
                value={bibliotecaSelected}
                defaultLibrary={null}
              />
            </Form.Group>
          </div>
        </Col>

        {/* Columna 3 */}
        <Col md={4} className="order-control-filters__col">
          <div className="order-control-filters__field">
            <Form.Group>
              <Form.Label className="order-control-filters__label">
                {t('filters.autor', { ns: 'backoffice' })}
              </Form.Label>
              <Form.Control
                type="text"
                value={filters.autor}
                onChange={(e) => setFilters({ ...filters, autor: e.target.value })}
                className="order-control-filters__input"
              />
            </Form.Group>
          </div>
          <div className="order-control-filters__field">
            <Form.Group>
              <Form.Label className="order-control-filters__label">
                {t('filters.estado', { ns: 'backoffice' })}
              </Form.Label>
              <Form.Select
                value={filters.estado}
                onChange={(e) => setFilters({ ...filters, estado: e.target.value })}
                className="order-control-filters__select"
              >
                {estadoOptions.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.value}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </div>
          <div className="order-control-filters__field">
            <div className="order-control-filters__reset-container">
              <Button onClick={handleResetFilters} color="terciary" role="button" className="order-control-filters__reset-btn">
                <FaArrowRotateRight className='me-2'/>
                <span className="order-control-filters__reset-text">{t('filters.resetFilters', { ns: 'backoffice' })}</span>
              </Button>
            </div>
          </div>
        </Col>
      </Row>
    </div>
  );
};

OrderControlFilters.propTypes = {
  filters: PropTypes.shape({
    ean: PropTypes.string,
    editorial: PropTypes.string,
    fechaPedido: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string, PropTypes.oneOf([null])]),
    titulo: PropTypes.string,
    biblioteca: PropTypes.string,
    autor: PropTypes.string,
    estado: PropTypes.string
  }).isRequired,
  setFilters: PropTypes.func.isRequired
};

export default OrderControlFilters;
